import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { Price } from '../price.js';
import { Currency } from '../currency.js';

export class SalesIncomeSummary implements IDataComparable, ICloneable {
  constructor(
    public offlineOrders: number,
    public offlineTickets: number,
    public onlineOrders: number,
    public onlineTickets: number,
    public promoterOrders: number,
    public promoterTickets: number,
    public protocolOrders: number,
    public protocolTickets: number,
    public totalOrders: number,
    public totalTickets: number,
    public offlineIncome: Price,
    public onlineIncome: Price,
    public promoterIncome: Price,
    public protocolIncome: Price,
    public totalIncome: Price,
  ) {}

  static empty(currency: Currency): SalesIncomeSummary {
    return new SalesIncomeSummary(
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        Price.zero(currency),
        Price.zero(currency),
        Price.zero(currency),
        Price.zero(currency),
        Price.zero(currency),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): SalesIncomeSummary {
    return new SalesIncomeSummary(
        MapUtils.get<number>(map, 'offlineOrders'),
        MapUtils.get<number>(map, 'offlineTickets'),
        MapUtils.get<number>(map, 'onlineOrders'),
        MapUtils.get<number>(map, 'onlineTickets'),
        MapUtils.get<number>(map, 'promoterOrders'),
        MapUtils.get<number>(map, 'promoterTickets'),
        MapUtils.get<number>(map, 'protocolOrders'),
        MapUtils.get<number>(map, 'protocolTickets'),
        MapUtils.get<number>(map, 'totalOrders'),
        MapUtils.get<number>(map, 'totalTickets'),
        Price.decode(MapUtils.get<string>(map, 'offlineIncome')),
        Price.decode(MapUtils.get<string>(map, 'onlineIncome')),
        Price.decode(MapUtils.get<string>(map, 'promoterIncome')),
        Price.decode(MapUtils.get<string>(map, 'protocolIncome')),
        Price.decode(MapUtils.get<string>(map, 'totalIncome')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'offlineOrders': this.offlineOrders,
      'offlineTickets': this.offlineTickets,
      'onlineOrders': this.onlineOrders,
      'onlineTickets': this.onlineTickets,
      'promoterOrders': this.promoterOrders,
      'promoterTickets': this.promoterTickets,
      'protocolOrders': this.protocolOrders,
      'protocolTickets': this.protocolTickets,
      'totalOrders': this.totalOrders,
      'totalTickets': this.totalTickets,
      'offlineIncome': this.offlineIncome.encode(),
      'onlineIncome': this.onlineIncome.encode(),
      'promoterIncome': this.promoterIncome.encode(),
      'protocolIncome': this.protocolIncome.encode(),
      'totalIncome': this.totalIncome.encode(),
    };
  }

  clone(): this {
    return new SalesIncomeSummary(
        this.offlineOrders,
        this.offlineTickets,
        this.onlineOrders,
        this.onlineTickets,
        this.promoterOrders,
        this.promoterTickets,
        this.protocolOrders,
        this.protocolTickets,
        this.totalOrders,
        this.totalTickets,
        this.offlineIncome.clone(),
        this.onlineIncome.clone(),
        this.promoterIncome.clone(),
        this.protocolIncome.clone(),
        this.totalIncome.clone(),
    ) as this;
  }

  isSame(other: SalesIncomeSummary): boolean {
    if(other === this) {
      return true;
    }

    return other.offlineOrders === this.offlineOrders &&
      other.offlineTickets === this.offlineTickets &&
      other.onlineOrders === this.onlineOrders &&
      other.onlineTickets === this.onlineTickets &&
      other.promoterOrders === this.promoterOrders &&
      other.promoterTickets === this.promoterTickets &&
      other.protocolOrders === this.protocolOrders &&
      other.protocolTickets === this.protocolTickets &&
      other.totalOrders === this.totalOrders &&
      other.totalTickets === this.totalTickets &&
      isSameData(other.offlineIncome, this.offlineIncome) &&
      isSameData(other.onlineIncome, this.onlineIncome) &&
      isSameData(other.promoterIncome, this.promoterIncome) &&
      isSameData(other.protocolIncome, this.protocolIncome) &&
      isSameData(other.totalIncome, this.totalIncome);
  }

  ensurePositive() {
    if(this.offlineOrders < 0) {
      this.offlineOrders = 0;
    }

    if(this.offlineTickets < 0) {
      this.offlineTickets = 0;
    }

    if(this.onlineOrders < 0) {
      this.onlineOrders = 0;
    }

    if(this.onlineTickets < 0) {
      this.onlineTickets = 0;
    }

    if(this.promoterOrders < 0) {
      this.promoterOrders = 0;
    }

    if(this.promoterTickets < 0) {
      this.promoterTickets = 0;
    }

    if(this.protocolOrders < 0) {
      this.protocolOrders = 0;
    }

    if(this.protocolTickets < 0) {
      this.protocolTickets = 0;
    }

    if(this.totalOrders < 0) {
      this.totalOrders = 0;
    }

    if(this.totalTickets < 0) {
      this.totalTickets = 0;
    }

    if(this.offlineIncome.value < 0) {
      this.offlineIncome = Price.zero(this.offlineIncome.currency);
    }

    if(this.onlineIncome.value < 0) {
      this.onlineIncome = Price.zero(this.onlineIncome.currency);
    }

    if(this.promoterIncome.value < 0) {
      this.promoterIncome = Price.zero(this.promoterIncome.currency);
    }

    if(this.protocolIncome.value < 0) {
      this.protocolIncome = Price.zero(this.protocolIncome.currency);
    }

    if(this.totalIncome.value < 0) {
      this.totalIncome = Price.zero(this.totalIncome.currency);
    }
  }
}
